import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Loader, ZoomIn, ZoomOut } from 'lucide-react';

// Set up the worker for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = ({ file, currentPage, setCurrentPage, setTotalPages }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(600);
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [pageInputValue, setPageInputValue] = useState(currentPage.toString());
  const containerRef = useRef(null);

  // Handle successful document loading
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setTotalPages(numPages);
    setIsLoading(false);
  };

  // Handle page navigation
  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Zoom controls
  const zoomIn = () => setScale(prevScale => Math.min(prevScale + 0.1, 2.0));
  const zoomOut = () => setScale(prevScale => Math.max(prevScale - 0.1, 0.5));
  
  // Handle page input changes
  const handlePageInputChange = (e) => {
    setPageInputValue(e.target.value);
  };
  
  // Handle page input submission
  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const newPage = parseInt(pageInputValue, 10);
    if (!isNaN(newPage) && newPage >= 1 && newPage <= numPages) {
      setCurrentPage(newPage);
    } else {
      // Reset to current page if invalid
      setPageInputValue(currentPage.toString());
    }
  };

  // Update input value when page changes
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);
  
  // Calculate container width for responsive sizing
  useEffect(() => {
    const updatePageDimensions = () => {
      if (containerRef.current) {
        // Use the container's width to determine PDF size
        const containerWidth = containerRef.current.clientWidth;
        
        // Make sure the page fits within the container with some margin
        // Use more of the available width to make the PDF larger
        const newWidth = Math.max(
          300,
          Math.min(containerWidth * 0.95, containerWidth - 20)
        );
        
        setPageWidth(newWidth);
      }
    };

    updatePageDimensions();
    window.addEventListener('resize', updatePageDimensions);
    return () => window.removeEventListener('resize', updatePageDimensions);
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-dark p-3 mb-2 flex justify-between items-center rounded-t-lg">
        <h2 className="text-xl font-semibold">Document Viewer</h2>
        <div className="flex space-x-2">
          <button 
            onClick={zoomOut}
            className="p-1 rounded-full hover:bg-dark-lighter text-primary"
            aria-label="Zoom out"
          >
            <ZoomOut size={18} />
          </button>
          <button 
            onClick={zoomIn}
            className="p-1 rounded-full hover:bg-dark-lighter text-primary"
            aria-label="Zoom in"
          >
            <ZoomIn size={18} />
          </button>
        </div>
      </div>

      <div 
        ref={containerRef} 
        className="flex-grow overflow-auto relative flex items-center justify-center bg-dark-darker h-[calc(100vh-185px)]"
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark bg-opacity-70 z-10">
            <Loader className="animate-spin text-primary" size={32} />
            <span className="ml-2">Loading document...</span>
          </div>
        )}

        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="flex items-center justify-center h-full w-full">
              <Loader className="animate-spin text-primary" size={32} />
            </div>
          }
          error={
            <div className="text-center p-4 border border-red-500 rounded-lg max-w-md mx-auto">
              <p className="text-red-500 font-medium">Error loading PDF document</p>
              <p className="text-sm text-gray-400 mt-1">
                There was a problem loading this document. Please try again.
              </p>
            </div>
          }
          className="flex flex-col items-center justify-center min-h-full"
        >
          <Page
            pageNumber={currentPage}
            width={pageWidth * scale}
            height={null} // Let height scale proportionally
            scale={scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={
              <div className="flex items-center justify-center h-64 w-full">
                <Loader className="animate-spin text-primary" size={24} />
              </div>
            }
            className="shadow-lg mb-2"
          />
        </Document>
      </div>

      <div className="flex justify-between items-center bg-dark p-3 mt-2 rounded-b-lg">
        <button
          onClick={goToPrevPage}
          disabled={currentPage <= 1}
          className={`p-2 rounded-full ${
            currentPage <= 1
              ? 'text-gray-600 cursor-not-allowed'
              : 'text-primary hover:bg-dark-lighter'
          }`}
          aria-label="Previous page"
        >
          <ChevronLeft size={20} />
        </button>

        <form onSubmit={handlePageInputSubmit} className="flex items-center">
          <span className="text-gray-400 text-sm mr-2">Page</span>
          <input
            type="text"
            value={pageInputValue}
            onChange={handlePageInputChange}
            onBlur={handlePageInputSubmit}
            className="w-12 bg-dark-lighter text-white border border-gray-600 rounded-md px-2 py-1 text-center text-sm"
            aria-label="Page number input"
          />
          <span className="text-gray-400 text-sm mx-2">of {numPages || '?'}</span>
        </form>

        <button
          onClick={goToNextPage}
          disabled={currentPage >= numPages}
          className={`p-2 rounded-full ${
            currentPage >= numPages
              ? 'text-gray-600 cursor-not-allowed'
              : 'text-primary hover:bg-dark-lighter'
          }`}
          aria-label="Next page"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default PDFViewer;